import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Check, Home, Building2 } from "lucide-react";
import { Reveal } from "@/components/Reveal";

const fokus = ["Steuerliche Behandlung von PV-Anlagen", "Einspeisevergütung (EEG)", "IAB nach §7g EStG"];

const nutzenPrivat = [
  "Vereinfachte steuerliche Behandlung bei kleinen Anlagen",
  "Planbare Einspeisevergütung über 20 Jahre",
  "Eigenverbrauch senkt laufende Energiekosten",
];

const nutzenGewerblich = [
  "Investitionsabzugsbetrag (IAB): bis zu 50 % vorab abziehbar",
  "Sonderabschreibung zusätzlich zur linearen AfA",
  "Staatlich garantierte Erträge als gewerbliche Einnahmen",
];

export const ChapterPhotovoltaik = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [-40, 40]);

  return (
    <section id="photovoltaik" ref={ref} data-testid="chapter-photovoltaic" className="bg-paper py-24 lg:py-36 grain relative">
      <div className="mx-auto grid max-w-7xl grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 px-4 sm:px-8 lg:px-16">
        <div className="lg:col-span-7 order-2 lg:order-1">
          <Reveal>
            <p className="eyebrow mb-4">Kapitel 02</p>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-petrol">
              Photovoltaik & Energie-Assets
            </h2>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-slate-600">
              Mit der Sonne nachhaltig Vermögen bilden: Photovoltaik-Anlagen
              liefern Erträge aus der Einspeisevergütung — und eröffnen je
              nach Gestaltung erhebliche steuerliche Spielräume.
            </p>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="mt-10 flex flex-wrap gap-3" data-testid="photovoltaik-fokus">
              {fokus.map((f) => (
                <span key={f} className="border border-forest/25 bg-sage/40 px-4 py-2 font-mono text-[11px] uppercase tracking-[0.15em] text-forest">
                  {f}
                </span>
              ))}
            </div>
          </Reveal>

          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-6" data-testid="photovoltaik-vergleich">
            <Reveal delay={0.15}>
              <div className="h-full border border-petrol/10 bg-white p-7 transition-shadow duration-300 hover:shadow-lg hover:shadow-petrol/5">
                <Home size={20} className="text-gold" />
                <h3 className="mt-4 font-display text-xl font-medium text-petrol">Private Nutzung</h3>
                <ul className="mt-4 space-y-3">
                  {nutzenPrivat.map((n) => (
                    <li key={n} className="flex items-start gap-3 text-sm leading-relaxed text-slate-600">
                      <Check size={15} className="mt-0.5 shrink-0 text-forest" />
                      {n}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
            <Reveal delay={0.25}>
              <div className="h-full border border-petrol bg-petrol p-7 transition-shadow duration-300 hover:shadow-xl hover:shadow-petrol/20">
                <Building2 size={20} className="text-gold" />
                <h3 className="mt-4 font-display text-xl font-medium text-paper">Gewerbliche Nutzung</h3>
                <ul className="mt-4 space-y-3">
                  {nutzenGewerblich.map((n) => (
                    <li key={n} className="flex items-start gap-3 text-sm leading-relaxed text-sage">
                      <Check size={15} className="mt-0.5 shrink-0 text-gold" />
                      {n}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          </div>

          <Reveal delay={0.2}>
            <div className="mt-10 border-l-2 border-gold bg-white p-8" data-testid="photovoltaik-nutzen">
              <p className="eyebrow mb-3">Ihr Nutzen</p>
              <p className="text-base leading-relaxed text-slate-700">
                Staatlich garantierte Erträge plus steuerliche Optimierung —
                Energie-Assets verbinden Planungssicherheit mit aktiver
                Steuergestaltung.
              </p>
            </div>
          </Reveal>
        </div>

        <div className="lg:col-span-5 order-1 lg:order-2">
          <div className="lg:sticky lg:top-28">
            <Reveal delay={0.1}>
              <div className="relative">
                <div className="absolute -top-4 -right-4 h-full w-full border border-forest/30" aria-hidden="true" />
                <div className="relative aspect-[4/5] overflow-hidden">
                  <motion.img
                    style={{ y: imgY }}
                    src="https://images.unsplash.com/photo-1668097613569-3405bb63262b?fm=jpg&q=85&auto=format&fit=crop"
                    alt="Photovoltaik-Module auf einem Dach"
                    className="h-[115%] w-full object-cover"
                    data-testid="photovoltaik-image"
                  />
                </div>
                <div className="absolute -left-5 bottom-10 animate-floaty bg-white/85 backdrop-blur-md border border-petrol/10 px-5 py-4 shadow-lg shadow-petrol/5" data-testid="photovoltaik-card-iab">
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-forest">IAB §7g EStG</p>
                  <p className="font-display text-2xl font-semibold text-petrol mt-1">bis zu 50&nbsp;%</p>
                  <p className="text-xs text-slate-500 mt-0.5">vor der Investition abziehbar</p>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};
